import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { addCountyLetterhead, loadCountyLogoDataUrl, addFooter } from "@/utils/payrollReconPdf";
import { loadCountyLetterhead } from "@/utils/countyReportExport";
import { resolveReportGeneratorMeta } from "@/utils/reportGeneratorMeta";

function money(n) {
  return (Number(n) || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function drawRemarksAndDownloader(doc, startY) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  let y = startY + 6;
  if (y > pageH - 55) {
    doc.addPage();
    y = 20;
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(15, 23, 42);
  doc.text("Remarks", margin, y);
  y += 4;
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.2);
  for (let i = 0; i < 5; i += 1) {
    y += 8;
    doc.line(margin, y, pageW - margin, y);
  }
  y += 12;
  const meta = resolveReportGeneratorMeta();
  const by = meta.payrollNo
    ? `Downloaded by: ${meta.name} (Employee No: ${meta.payrollNo})`
    : `Downloaded by: ${meta.name}`;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  doc.setTextColor(71, 85, 105);
  doc.text(by, margin, y);
  doc.text("System generated report", pageW - margin, y, { align: "right" });
}

async function prepareDoc(title, subtitle) {
  const letterhead = await loadCountyLetterhead();
  await loadCountyLogoDataUrl(letterhead);
  const meta = resolveReportGeneratorMeta();
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const startY = addCountyLetterhead(doc, {
    title,
    subtitle,
    letterhead,
    generatedBy: meta.name,
    payrollNumber: meta.payrollNo,
    systemGenerated: true,
  });
  return { doc, letterhead, startY };
}

function saveOrView(doc, filename, view) {
  if (view) {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  doc.save(filename);
}

export async function exportArSchedulePdf({ caze, schedule, view = false }) {
  const kind = String(caze.case_kind || "").toLowerCase() === "recovery" ? "Recovery" : "Arrears";
  const { doc, letterhead, startY } = await prepareDoc(
    `${kind} payment schedule`,
    `${caze.employee_name || ""} · Emp No ${caze.emp_no || ""}`
  );
  autoTable(doc, {
    startY,
    theme: "grid",
    head: [["Field", "Details"]],
    body: [
      ["Employee number", caze.emp_no || ""],
      ["Name", caze.employee_name || ""],
      ["National ID", caze.national_id || ""],
      ["Department", caze.department_name || ""],
      ["Designation", caze.designation || ""],
      ["Type", caze.type_label || caze.type_code || ""],
      ["Principal (KES)", money(caze.principal)],
      ["Amount per month (KES)", money(caze.monthly_amount)],
      ["Number of months", String(caze.months || (schedule || []).length)],
    ],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [12, 74, 110] },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 55 } },
  });
  const rows = (schedule || []).map((r) => [
    r.period_no,
    r.period_label,
    r.scheduled_date || "",
    money(r.due_amount),
  ]);
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || startY) + 6,
    theme: "striped",
    head: [["#", "Month", "Scheduled date", "Amount due (KES)"]],
    body: rows.length ? rows : [["—", "No schedule lines", "", ""]],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [12, 74, 110] },
  });
  drawRemarksAndDownloader(doc, doc.lastAutoTable?.finalY || startY);
  addFooter(doc, letterhead);
  const fn = `${kind.toLowerCase()}-schedule-${caze.emp_no || caze.id}.pdf`;
  saveOrView(doc, fn, view);
}

export async function exportArPaymentReportPdf({ caze, payments, view = false }) {
  const isRec = String(caze.case_kind || "").toLowerCase() === "recovery";
  const title = isRec ? "Recovery payment report" : "Arrears payment report";
  const { doc, letterhead, startY } = await prepareDoc(
    title,
    `${caze.employee_name || ""} · Emp No ${caze.emp_no || ""}`
  );
  autoTable(doc, {
    startY,
    theme: "grid",
    head: [["Field", "Details"]],
    body: [
      ["Employee number", caze.emp_no || ""],
      ["Name", caze.employee_name || ""],
      ["Department", caze.department_name || ""],
      ["Type", caze.type_label || caze.type_code || ""],
      ["Principal (KES)", money(caze.principal)],
      ["Paid / recovered (KES)", money(caze.paid_total)],
      ["Balance (KES)", money(caze.balance)],
    ],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [12, 74, 110] },
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 55 } },
  });
  const head = isRec
    ? [["#", "Recovery month", "Recovery method", "Date recovered", "Amount recovered (KES)", "Balance (KES)"]]
    : [["#", "Month", "Date paid", "Amount paid (KES)", "Balance (KES)"]];
  const body = (payments || []).map((p, i) =>
    isRec
      ? [i + 1, p.period_label || "", p.method || "", p.paid_at || "", money(p.amount), money(p.balance_after)]
      : [i + 1, p.period_label || "", p.paid_at || "", money(p.amount), money(p.balance_after)]
  );
  autoTable(doc, {
    startY: (doc.lastAutoTable?.finalY || startY) + 6,
    theme: "striped",
    head,
    body: body.length ? body : [["—", "No payments recorded yet", "", "", ""]],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [12, 74, 110] },
  });
  drawRemarksAndDownloader(doc, doc.lastAutoTable?.finalY || startY);
  addFooter(doc, letterhead);
  saveOrView(doc, `payment-report-${caze.emp_no || caze.id}.pdf`, view);
}
